import { useEffect, useState } from "react";
import { CarFront, Fuel, Settings2, Gauge, X, MessageCircle } from "lucide-react";
import { useLang } from "@/i18n";
import { API, waLink, submitRequest } from "@/config";
import { Field, Input, Textarea, Gdpr, Honeypot, SuccessBox, SubmitBtn, useFormState } from "@/components/FormBits";
import { toast } from "sonner";

const fmtPrice = (n) => `€ ${Number(n).toLocaleString("nl-BE")}`;
const imgSrc = (url) => (url?.startsWith("/") ? `${API.replace("/api", "")}${url}` : url);

function SaleCard({ car, onInterest }) {
  const { t } = useLang();
  return (
    <div data-testid={`sale-card-${car.id}`} className="card-dark group hover:border-[#E11D48]/50 transition-colors duration-300 flex flex-col">
      <div className="relative h-52 overflow-hidden">
        {car.image_url ? (
          <img src={imgSrc(car.image_url)} alt={`${car.brand} ${car.model}`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full bg-zinc-800 flex items-center justify-center"><CarFront size={40} className="text-zinc-600" /></div>
        )}
        <span data-testid={`sale-status-${car.id}`}
          className={`absolute top-3 right-3 text-[10px] font-bold uppercase tracking-widest rounded-full px-3 py-1 ${car.available ? "bg-[#22C55E]/90 text-white" : "bg-zinc-700/90 text-zinc-300"}`}>
          {car.available ? t("sale.available") : t("sale.sold")}
        </span>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-heading font-bold text-lg text-white">{car.brand} {car.model}</h3>
          <span className="font-heading text-xl font-extrabold text-[#E11D48] whitespace-nowrap" data-testid={`sale-price-${car.id}`}>{fmtPrice(car.price)}</span>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-zinc-400">
          <span className="flex items-center gap-1"><Settings2 size={12} className="text-[#E11D48]" />{car.year}</span>
          <span className="flex items-center gap-1"><Fuel size={12} className="text-[#E11D48]" />{car.fuel}</span>
          <span className="flex items-center gap-1"><Gauge size={12} className="text-[#E11D48]" />{Number(car.km).toLocaleString("nl-BE")} km</span>
          <span className="col-span-3">{car.transmission}</span>
        </div>
        {car.description && <p className="mt-3 text-xs text-zinc-400 leading-relaxed border-t border-zinc-800 pt-3">{car.description}</p>}
        <button onClick={() => onInterest(car)} disabled={!car.available} data-testid={`sale-interest-${car.id}`}
          className="btn-red w-full mt-auto pt-2.5 !py-2.5 text-sm disabled:opacity-40 disabled:cursor-not-allowed">
          {t("sale.interest")}
        </button>
      </div>
    </div>
  );
}

function InterestModal({ car, onClose }) {
  const { t } = useLang();
  const [data, set] = useFormState({ name: "", phone: "", email: "", car: `${car.brand} ${car.model} (${car.year}) — ${fmtPrice(car.price)}`, remarks: "", website: "", gdpr: false });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await submitRequest("sale", data); setDone(true); }
    catch (err) { toast.error(err.message); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="sale-modal">
      <div className="card-dark w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto rounded-b-none sm:rounded-b-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading font-bold text-xl text-white">{t("sale.modalTitle")}</h3>
          <button onClick={onClose} data-testid="sale-modal-close" className="p-1.5 text-zinc-400 hover:text-white"><X size={20} /></button>
        </div>
        {done ? <SuccessBox message={t("sale.success")} testid="sale-success" /> : (
          <form onSubmit={submit} className="grid gap-4" data-testid="sale-form">
            <Honeypot value={data.website} onChange={set("website")} />
            <Field label={t("rental.chosen")}><Input value={data.car} readOnly className="input-dark opacity-70" data-testid="s-car" /></Field>
            <Field label={t("form.name")} required><Input required value={data.name} onChange={set("name")} data-testid="s-name" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label={t("form.phone")} required><Input required type="tel" value={data.phone} onChange={set("phone")} data-testid="s-phone" /></Field>
              <Field label={t("form.email")} required><Input required type="email" value={data.email} onChange={set("email")} data-testid="s-email" /></Field>
            </div>
            <Field label={t("form.remarks")}><Textarea value={data.remarks} onChange={set("remarks")} data-testid="s-remarks" /></Field>
            <Gdpr checked={data.gdpr} onChange={(v) => set("gdpr")({ target: { value: v } })} />
            <SubmitBtn loading={loading} label={t("sale.send")} testid="s-submit" />
            <a href={waLink(t("wa.sale"))} target="_blank" rel="noreferrer" className="btn-wa" data-testid="s-whatsapp">
              <MessageCircle size={18} />WhatsApp
            </a>
          </form>
        )}
      </div>
    </div>
  );
}

export default function TeKoop() {
  const { t } = useLang();
  const [cars, setCars] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API}/sale-vehicles`).then((r) => r.json()).then(setCars).catch(() => {});
  }, []);

  return (
    <div className="container-x py-14" data-testid="tekoop-page">
      <p className="overline-red">Milano Worx</p>
      <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-white">{t("sale.title")}</h1>
      <p className="mt-3 text-zinc-400 max-w-2xl">{t("sale.sub")}</p>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {cars.map((c) => <SaleCard key={c.id} car={c} onInterest={setSelected} />)}
        {cars.length === 0 && <p className="text-zinc-500 col-span-full py-10 text-center">—</p>}
      </div>

      <div className="mt-12 card-dark p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <p className="text-sm text-zinc-400 max-w-xl">{t("sale.sub")}</p>
        <a href={waLink(t("wa.sale"))} target="_blank" rel="noreferrer" className="btn-wa shrink-0" data-testid="sale-whatsapp-cta">
          <MessageCircle size={18} />WhatsApp
        </a>
      </div>

      {selected && <InterestModal car={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
